import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import DebtNavbar from "../components/layout/DebtNavbar";
import Footer from "../components/layout/Footer";
import Seo from "../components/Seo";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { ChevronField, Comprehensive, Clarity, Knowledge, Network, Idea } from "../components/common/BrandShapes";
import { Pill } from "../components/common/Primitives";
import { HeroProductCards, FinancialCalculators, BankingPartners, PartnersMap } from "../components/debt/DebtLandingExtras";
import { VideoPreview } from "../components/common/VideoLightbox";
import { useHeroVideoConfig } from "../lib/useHeroVideoConfig";
import {
  Landmark, Briefcase, Wallet, Building2, TrendingUp, Shield,
  FileText, HandCoins, Timer, Sparkles, Handshake, Users,
  ArrowRight, CheckCircle2, ChevronDown, ChevronRight, IndianRupee, Calculator,
  Home, Car, GraduationCap, PlaneTakeoff, HeartHandshake, Gift, Zap, PhoneCall,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */
const PRODUCTS_INDIVIDUAL = [
  { key: "home_loan", icon: Home, title: "Home Loan", tag: "₹5 L – ₹10 Cr", pitch: "Own your dream home with rates from 8.35% p.a. Compare offers from 25+ banks and NBFCs in one place." },
  { key: "car_loan", icon: Car, title: "Car Loan", tag: "₹1 L – ₹1 Cr", pitch: "Drive your dream car home this weekend. 100% on-road funding, rates from 8.75% p.a." },
  { key: "personal_loan", icon: Wallet, title: "Personal Loan", tag: "₹50 K – ₹40 L", pitch: "Wedding, vacation, medical, home renovation — get funds credited in as little as 24 hours." },
  { key: "education_loan", icon: GraduationCap, title: "Education Loan", tag: "₹1 L – ₹1.5 Cr", pitch: "Fund your degree abroad or in India. No collateral for loans up to ₹40 L, moratorium during study." },
  { key: "gold_loan", icon: HandCoins, title: "Gold Loan", tag: "₹10 K – ₹2 Cr", pitch: "Unlock the value of your gold in 30 minutes. Rates from 8.85% p.a. with the safest RBI-regulated NBFCs." },
  { key: "lap_ind", icon: Building2, title: "Loan Against Property", tag: "₹5 L – ₹5 Cr", pitch: "Fund big life goals — wedding, medical, business — against your residential or commercial property." },
];
const PRODUCTS_BUSINESS = [
  { key: "working_capital", icon: Wallet, title: "Working Capital", tag: "₹25 L – ₹25 Cr", pitch: "CC / OD lines against receivables, GST or banking — for day-to-day operations." },
  { key: "term_loan", icon: Landmark, title: "Term Loan", tag: "₹50 L – ₹100 Cr", pitch: "Structured EMI-based debt for expansion, CapEx or refinancing existing obligations." },
  { key: "business_loan", icon: Briefcase, title: "Unsecured Business Loan", tag: "₹5 L – ₹75 L", pitch: "Collateral-free loans for MSMEs and self-employed professionals. Approval in 48 hours." },
  { key: "lap_biz", icon: Building2, title: "Loan Against Property", tag: "₹25 L – ₹250 Cr", pitch: "Unlock capital against commercial or residential property at attractive rates." },
  { key: "invoice_discounting", icon: FileText, title: "Invoice Discounting", tag: "₹10 L – ₹50 Cr", pitch: "Discount blue-chip receivables and convert 90-day cycles into same-day cash." },
  { key: "structured", icon: HandCoins, title: "Structured / Bridge / RBF", tag: "₹5 Cr – ₹500 Cr", pitch: "Mezzanine, bridge, revenue-based financing and private credit for complex requirements." },
];
const PRODUCTS = PRODUCTS_BUSINESS;  // legacy compat
const PURPOSES = [
  // Individual products
  { key: "home_loan", label: "Home Loan" },
  { key: "car_loan", label: "Car Loan / Vehicle" },
  { key: "personal_loan", label: "Personal / Wedding / Vacation" },
  { key: "education_loan", label: "Education" },
  { key: "lap", label: "Loan Against Property" },
  { key: "loan_against_securities", label: "Loan Against Securities" },
  // Business products
  { key: "business_loan", label: "Unsecured Business Loan" },
  { key: "working_capital", label: "Business Working Capital" },
  { key: "cc_od", label: "CC / OD Facility" },
  { key: "term_loan", label: "Term Loan" },
  { key: "equipment_finance", label: "Equipment Finance" },
  { key: "project_finance", label: "Project Finance" },
  { key: "construction_finance", label: "Construction Finance" },
  { key: "supply_chain_finance", label: "Supply Chain Finance" },
  { key: "invoice_discounting", label: "Invoice / Receivable Financing" },
  { key: "structured_finance", label: "Structured Finance" },
  { key: "private_credit", label: "Private Credit" },
  { key: "expansion", label: "Business Expansion / CapEx" },
  { key: "refinance", label: "Refinance / Balance Transfer" },
  { key: "other", label: "Other" },
];
const SECURITY_TYPES = [
  { key: "unsecured", label: "Unsecured" },
  { key: "hypothecation", label: "Hypothecation" },
  { key: "immovable", label: "Immovable Property" },
  { key: "pledge", label: "Pledge (shares/securities)" },
  { key: "personal_guarantee", label: "Personal Guarantee" },
  { key: "dsra", label: "DSRA / Escrow" },
  { key: "other", label: "Other" },
];
const STEPS = [
  { n: "01", title: "Share your requirement", body: "Fill a 90-second brief. Amount, purpose, tenure, security — that's all we need to open a case." },
  { n: "02", title: "Assigned to a specialist", body: "A named debt specialist rings you within one business day. Bank-style Personal Discussion, no bot loops." },
  { n: "03", title: "Documents & credit", body: "Curated document checklist per product. Bureau + banking + GST analysed. You'll see every flag we surface." },
  { n: "04", title: "Lender shortlist", body: "We pitch your case to a hand-picked set of lenders — no scattergun. You see every submission and its status live." },
  { n: "05", title: "Offers & mandate", body: "Compare sanctioned offers side-by-side. Sign the mandate on our secure e-sign — never any signing fee for debt clients." },
  { n: "06", title: "Disbursal", body: "Documentation, conditions precedent, disbursal. Invoicing is generated only after money hits your account." },
];
const FAQ = [
  { q: "Is VenturaZ a lender?", a: "No. VenturaZ is a licensed loan advisor. We don't lend money ourselves — we compare offers from 85+ banks, NBFCs and private credit funds, negotiate on your behalf, and manage the paperwork through disbursal. You always sign directly with the lender, never with us." },
  { q: "How much does VenturaZ charge me?", a: "Zero platform fee. We don't charge you a paisa for consultation, CIBIL check or mandate signing. We're paid a success fee by the lender — only if your loan actually disburses." },
  { q: "Do you help individuals or only businesses?", a: "Both. Individuals raise home, car, personal, education, wedding, gold and property loans. Businesses raise working capital, term loans, LAP, invoice discounting, RBF and structured credit — up to ₹500 Cr." },
  { q: "How is the free CIBIL check different from checking on the CIBIL website?", a: "Same score, no cost, plus a walkthrough. You get your free credit report worth ₹999, plus an advisor call to explain what each flag means and exactly how to improve your score before applying." },
  { q: "How long does a typical loan take?", a: "Personal loans: 24–72 hours. Home / car loans: 7–14 days. Business working-capital / term loans: 20–45 days. LAP and structured facilities: 45–90 days. We keep you posted at every stage — no black-box waiting." },
  { q: "Is my data safe?", a: "Yes. VenturaZ is DPDP-compliant, ISO 27001-certified partners only, and every credit pull is with explicit consent. We share your documents only with the lenders you approve — never dropped into a broker distribution list." },
];

const fmtCr = (n) => (n >= 1e7 ? `₹${(n / 1e7).toFixed(n / 1e7 < 10 ? 2 : 1)} Cr` : `₹${(n / 1e5).toFixed(0)} L`);

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function RaiseDebtLanding() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const shouldPrefill = searchParams.get("prefill") === "1";
  const hasPurposeDeepLink = !!searchParams.get("purpose");
  const [openFaq, setOpenFaq] = useState(0);
  const [suit, setSuit] = useState(null);

  useEffect(() => {
    if (user?.role === "startup") {
      api.get("/debt/suitability").then((r) => setSuit(r.data)).catch(() => {});
    }
  }, [user]);

  // One-click deep link — if the user landed here from the Suitability
  // tile with `?prefill=1`, or from a product page with `?purpose=...`,
  // jump straight to the lead form so they can review + submit in one motion.
  useEffect(() => {
    if (!shouldPrefill && !hasPurposeDeepLink) return;
    const el = document.getElementById("lead-form");
    if (el) {
      // Slight delay so the mini-form has hydrated from /debt/prefill-me.
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, [shouldPrefill, hasPurposeDeepLink]);

  return (
    <div className="min-h-screen text-ink debt-shell" data-testid="raise-debt-page">
      <Seo
        title="VenturaZ · Compare Home Loans, Car Loans, Personal Loans, Business Loans in India"
        description="India's honest loan advisor. Compare offers from 85+ banks and NBFCs — home, car, personal, education, business. Free CIBIL check worth ₹999. Free advisor consultation. Zero platform fees."
        path="/money"
      />
      <DebtNavbar />

      {/* Vertical ribbon — makes it obvious you've entered the debt vertical.
          Same brand voice, distinct palette. */}
      <div className="debt-accent-band h-[3px]" aria-hidden="true" />
      <div className="bg-[#FBFAF5] border-b border-[#B8860B]/25 py-1.5" data-testid="debt-vertical-ribbon">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.28em]">
          <span className="debt-copper">VenturaZ Money · A VenturaZ vertical</span>
          <span className="text-ink-muted hidden sm:inline">Not a lender · Loan advisor licensed IRDAI-inspired code · Zero platform fee</span>
        </div>
      </div>

      <Hero user={user} suit={suit} />
      <HeroProductCards />
      <TrustStrip />
      <BankingPartners />
      <DreamsRibbon />
      <Products />
      <FreeCibilCallout />
      <HowItWorks />
      <MoneyVideoOverview />
      <PartnersMap />
      <EligibilityAndDocs />
      <FinancialCalculators />
      <SocialProof />
      <FaqSection openFaq={openFaq} setOpenFaq={setOpenFaq} />
      <FinalCta />
      <Footer />
      <Toaster richColors position="top-right" />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Hero — split: pitch left, mini-form right                           */
/* ------------------------------------------------------------------ */
function Hero({ user, suit }) {
  const videoCfg = useHeroVideoConfig("money");
  return (
    <section className="relative brand-section-primary overflow-hidden" data-testid="raise-debt-hero">
      <div className="absolute inset-y-0 right-0 w-[45%] pointer-events-none" aria-hidden="true">
        <ChevronField color="#FFFFFF" opacity={0.16} className="w-full h-full" />
      </div>
      <Comprehensive size={220} color="#FFFFFF" opacity={0.14} strokeWidth={1.4}
        className="absolute top-16 left-8 hidden md:block pointer-events-none" />
      <Knowledge size={150} color="#FFD700" opacity={0.25} strokeWidth={1.4}
        className="absolute bottom-16 left-[40%] hidden lg:block pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-16 lg:pt-28 lg:pb-24 grid grid-cols-12 gap-8">
        {/* Pitch */}
        <div className="col-span-12 lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="text-[10px] font-mono tracking-[0.22em] uppercase text-white/80 mb-4" data-testid="raise-debt-overline">
              VENTURAZ · INDIA&apos;S HONEST LOAN ADVISOR
            </div>
            <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-[64px] leading-[0.98] drop-shadow-[0_2px_16px_rgba(0,0,0,0.15)]" data-testid="raise-debt-headline">
              Fulfil every dream,<br /><span className="text-gold">on your terms.</span>
            </h1>
            <p className="mt-6 text-white/85 text-base md:text-lg leading-relaxed max-w-2xl" data-testid="raise-debt-subheadline">
              Home, car, wedding, vacation, education, business — whatever the ambition,
              VenturaZ helps you compare and choose the right loan from 85+ banks, NBFCs
              and private credit funds. We&apos;re advisors, not lenders. Zero platform fee.
            </p>
            <div className="mt-6 flex flex-wrap gap-2" data-testid="raise-debt-offer-chips">
              <OfferChip icon={Gift} label="Free CIBIL check" sub="worth ₹999" />
              <OfferChip icon={PhoneCall} label="Free advisor call" sub="within 1 business day" />
              <OfferChip icon={Zap} label="Fastest disbursals" sub="24-72 hours" />
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#lead-form" className="px-6 py-3 bg-gold text-navy font-heading font-semibold hover:bg-gold-hover transition-all shadow-[0_10px_30px_rgba(255,215,0,0.35)] rounded-md" data-testid="raise-debt-hero-cta">
                Get free consultation &nbsp;<ArrowRight className="inline" size={16} />
              </a>
              <a href="#products" className="px-6 py-3 border border-white/40 text-white font-heading hover:bg-white/10 transition-all rounded-md" data-testid="raise-debt-products-cta">
                Explore loan products
              </a>
              {/* Copper watch pill — CMS-driven from /admin/hero-pulse.
                  Hidden entirely when Super Admin flips the Enabled
                  toggle off. */}
              {videoCfg.enabled !== false && (
                <VideoPreview
                  size="thumb"
                  palette="copper"
                  src={videoCfg.url || undefined}
                  title={videoCfg.title}
                  description={videoCfg.description}
                  duration={videoCfg.duration}
                  testId="raise-debt-hero-video-thumb"
                />
              )}
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl">
              <HeroStat label="Disbursed FY" value="₹1,240 Cr" />
              <HeroStat label="Lenders on tap" value="85+" />
              <HeroStat label="Happy borrowers" value="12,000+" />
            </div>
          </motion.div>
        </div>

        {/* Mini-lead form */}
        <div className="col-span-12 lg:col-span-5">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
            <MiniForm suit={suit} user={user} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
function HeroStat({ label, value }) {
  return (
    <div>
      <div className="font-display text-gold text-2xl md:text-3xl">{value}</div>
      <div className="text-[10px] font-mono tracking-widest uppercase text-white/70 mt-1">{label}</div>
    </div>
  );
}
function OfferChip({ icon: Icon, label, sub }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full bg-white/12 backdrop-blur-sm border border-white/25 px-3 py-1.5">
      <Icon size={13} className="text-gold shrink-0" />
      <div className="leading-tight">
        <div className="text-xs font-heading text-white">{label}</div>
        <div className="text-[9px] font-mono text-white/70 uppercase tracking-widest">{sub}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Mini form — the money maker                                         */
/* ------------------------------------------------------------------ */
function MiniForm({ suit, user }) {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // URL param wins; else the persisted `debt_audience` on the user record
  // (populated on the `/money/onboard` step); else default to individual.
  const urlAudience = searchParams.get("audience");
  const urlPurpose = searchParams.get("purpose");
  const [audienceHint, setAudienceHint] = useState(
    urlAudience === "business" || urlAudience === "individual" ? urlAudience : null
  );
  // Business purposes cover the top ~half of the PURPOSES enum; individual
  // covers the rest. Kept in one place so future purpose adds are wired in
  // both the audience default and the Products tab in the same commit.
  const _defaultPurpose = (aud) => (aud === "business" ? "working_capital" : "personal_loan");
  // URL param `purpose` (from a product deep-link) trumps everything else.
  const _initialPurpose = () => {
    if (urlPurpose && PURPOSES.some((p) => p.key === urlPurpose)) return urlPurpose;
    return _defaultPurpose(audienceHint);
  };
  const [form, setForm] = useState({
    amount_inr: "",
    purpose: _initialPurpose(),
    tenure_months: 60,
    security_type: "unsecured",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    company_name: "",
  });
  const [busy, setBusy] = useState(false);

  // Fetch the persisted audience iff not supplied via URL.
  useEffect(() => {
    if (audienceHint) return;
    if (!user) return;
    api.get("/debt/onboard/audience").then((r) => {
      const a = r?.data?.debt_audience;
      if (a === "business" || a === "individual") {
        setAudienceHint(a);
        setForm((f) => (f.purpose && f.purpose !== "personal_loan" ? f : { ...f, purpose: _defaultPurpose(a) }));
      }
    }).catch(() => { /* silent — non-auth or endpoint missing */ });
  }, [user, audienceHint]);

  useEffect(() => {
    if (user) setForm((f) => ({
      ...f,
      contact_name: f.contact_name || user.name || "",
      contact_email: f.contact_email || user.email || "",
    }));
    if (user?.role === "startup") {
      // One-click prefill: pull the startup's saved profile so the entire
      // form (company, PAN, GST, contact, suggested amount, sector) is
      // pre-hydrated. Falls back gracefully if the endpoint 404s.
      api.get("/debt/prefill-me").then((r) => {
        const p = r?.data?.prefill || {};
        setForm((f) => {
          // Explicit "prefill wins" merge: for any field returned by the
          // API, prefer the API value over the component defaults or
          // stale user.name. Only keep the user's typed value if the API
          // returned nothing for that field (iter-115 fix).
          const pick = (apiVal, current) => (apiVal != null && apiVal !== "" ? apiVal : current);
          return {
            ...f,
            company_name: pick(p.company_name, f.company_name),
            contact_name: pick(p.contact_name, f.contact_name),
            contact_email: pick(p.contact_email, f.contact_email),
            contact_phone: pick(p.contact_phone, f.contact_phone),
            amount_inr: pick(p.amount_inr, f.amount_inr),
            purpose: pick(p.purpose === "Working Capital" ? "working_capital" : p.purpose, f.purpose),
            tenure_months: pick(p.tenure_months, f.tenure_months),
            security_type: pick(p.security_type, f.security_type),
            _prefilled: !!(p.company_name || p.amount_inr),
          };
        });
      }).catch(() => {});
    }
  }, [user]);
  useEffect(() => {
    if (suit?.suggested_amount_inr) {
      setForm((f) => f.amount_inr ? f : { ...f, amount_inr: suit.suggested_amount_inr });
    }
  }, [suit]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!user) { toast.info("Sign in first so we can save your case."); nav("/signup?next=/raise-debt"); return; }
    if (!form.company_name || !form.contact_name || !form.contact_email || Number(form.amount_inr) <= 0) {
      toast.error("Fill company, contact, and requested amount.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/debt/cases", {
        ...form,
        amount_inr: Number(form.amount_inr),
        tenure_months: Number(form.tenure_months) || 24,
      });
      toast.success("Case created! A specialist will call you shortly.");
      nav(`/my-debt-cases/${r.data.case_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div id="lead-form" className="bg-white rounded-lg shadow-[0_25px_80px_rgba(0,0,0,0.25)] border border-white/40 overflow-hidden" data-testid="raise-debt-mini-form">
      <div className="px-5 py-3 bg-navy text-white border-b border-white/10 flex items-center gap-2">
        <Sparkles size={14} className="text-gold" />
        <div className="text-[10px] font-mono tracking-widest uppercase">Free consultation · No obligation</div>
      </div>
      <div className="p-5 space-y-3">
        {form._prefilled && (
          <div
            data-testid="raise-debt-prefill-banner"
            className="flex items-start gap-2 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-900"
          >
            <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
            <span>
              We pre-filled this from your VenturaZ startup profile — review the amount + purpose and hit
              <b> Get free consultation</b>.
            </span>
          </div>
        )}
        <TightField label="Company / your name (as applicable)" value={form.company_name} onChange={set("company_name")} testId="hero-company" />
        <div className="grid grid-cols-2 gap-3">
          <TightField label="Requested amount (₹)" type="number" value={form.amount_inr} onChange={set("amount_inr")} testId="hero-amount" placeholder="e.g. 2500000" />
          <TightField label="Tenure (months)" type="number" value={form.tenure_months} onChange={set("tenure_months")} testId="hero-tenure" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TightSelect label="Purpose" value={form.purpose} onChange={set("purpose")} options={PURPOSES} testId="hero-purpose" />
          <TightSelect label="Security" value={form.security_type} onChange={set("security_type")} options={SECURITY_TYPES} testId="hero-security" />
        </div>
        <TightField label="Your full name" value={form.contact_name} onChange={set("contact_name")} testId="hero-name" />
        <div className="grid grid-cols-2 gap-3">
          <TightField label="Email" type="email" value={form.contact_email} onChange={set("contact_email")} testId="hero-email" />
          <TightField label="Phone" value={form.contact_phone} onChange={set("contact_phone")} testId="hero-phone" />
        </div>
        <button onClick={submit} disabled={busy}
          className="w-full mt-1 py-3 bg-gold text-navy font-heading font-semibold rounded hover:bg-gold-hover disabled:opacity-60 shadow-[0_10px_25px_rgba(255,215,0,0.25)]"
          data-testid="hero-submit">
          {busy ? "Booking your consultation…" : "Get free consultation + CIBIL"}
        </button>
        <div className="text-[10px] font-mono text-ink-muted text-center">
          Free CIBIL worth ₹999 · Zero platform fees · Data stays in India (DPDP compliant)
        </div>
      </div>
      {suit?.eligible && (
        <div className="px-5 py-3 border-t border-navy/[0.06] bg-gold/10">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8A6600] mb-1 flex items-center gap-1">
            <Sparkles size={10} /> Personalised suggestion (from your profile)
          </div>
          <div className="text-xs text-ink">
            Indicative ticket: <b>₹{Number(suit.suggested_amount_inr || 0).toLocaleString("en-IN")}</b> · Suggested: <b>{(suit.suggested_products || [])[0] || "Working Capital"}</b>
          </div>
        </div>
      )}
    </div>
  );
}

function TightField({ label, testId, ...rest }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</label>
      <input {...rest} data-testid={testId}
        className="w-full mt-0.5 px-3 py-2 border border-navy/15 rounded text-sm text-ink bg-white focus:outline-none focus:border-navy/50" />
    </div>
  );
}
function TightSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</label>
      <select value={value} onChange={onChange} data-testid={testId}
        className="w-full mt-0.5 px-3 py-2 border border-navy/15 rounded text-sm text-ink bg-white focus:outline-none focus:border-navy/50">
        {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Trust strip                                                         */
/* ------------------------------------------------------------------ */
function TrustStrip() {
  const marks = [
    { icon: Shield, label: "Advisor, not lender" },
    { icon: Gift, label: "Free CIBIL worth ₹999" },
    { icon: PhoneCall, label: "Free advisor consultation" },
    { icon: Handshake, label: "85+ lenders on tap" },
    { icon: Wallet, label: "Zero platform fees" },
  ];
  return (
    <section className="brand-section-alt border-y border-navy/[0.06]" data-testid="raise-debt-trust">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6">
        <div className="flex flex-wrap items-center justify-around gap-y-3 gap-x-6 text-ink/80">
          {marks.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2 text-xs md:text-sm font-heading">
              <Icon size={16} className="text-navy" />
              <span>{label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Dreams ribbon — aspirational icons/labels                          */
/* ------------------------------------------------------------------ */
function DreamsRibbon() {
  const DREAMS = [
    { icon: Home, label: "Buy your dream home" },
    { icon: Car, label: "Drive your dream car" },
    { icon: PlaneTakeoff, label: "Fund that dream vacation" },
    { icon: HeartHandshake, label: "Plan the perfect wedding" },
    { icon: GraduationCap, label: "Study at your dream school" },
    { icon: Briefcase, label: "Grow your business" },
  ];
  return (
    <section className="brand-section-alt" data-testid="raise-debt-dreams">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
        <div className="max-w-3xl mb-6">
          <div className="text-[10px] font-mono tracking-widest uppercase text-ink-muted mb-2">DREAMS · WE MAKE THEM AFFORDABLE</div>
          <h2 className="font-display text-3xl sm:text-4xl text-ink leading-tight">
            What are you saving for?
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {DREAMS.map(({ icon: Icon, label }, i) => (
            <a key={i} href="#lead-form" data-testid={`raise-debt-dream-${i}`}
              className="border border-navy/10 rounded p-4 bg-white/70 hover:bg-white hover:border-navy/25 transition-all group text-center">
              <div className="w-11 h-11 mx-auto rounded-full bg-navy/[0.06] flex items-center justify-center group-hover:bg-gold/25 transition-colors">
                <Icon size={20} className="text-navy" />
              </div>
              <div className="mt-2 font-heading text-sm text-ink leading-tight">{label}</div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Products with audience tabs                                         */
/* ------------------------------------------------------------------ */
function Products() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("audience") === "business" ? "business" : "individual";
  const [tab, setTab] = useState(initialTab);
  const list = tab === "individual" ? PRODUCTS_INDIVIDUAL : PRODUCTS_BUSINESS;
  return (
    <section id="products" className="brand-section-alt border-t border-navy/[0.06]" data-testid="raise-debt-products">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="max-w-3xl mb-8">
          <div className="text-[10px] font-mono tracking-widest uppercase text-ink-muted mb-2">
            PRODUCTS · YOU CHOOSE, WE COMPARE
          </div>
          <h2 className="font-display text-4xl sm:text-5xl text-ink leading-tight">
            Every loan product, under one roof.
          </h2>
          <p className="mt-3 text-ink-soft text-base leading-relaxed">
            We&apos;re advisors — not lenders. We shortlist the right lender for your profile,
            negotiate on your behalf, and stay through disbursal. You never pay us a rupee.
          </p>
        </div>
        <div className="flex gap-2 mb-6" data-testid="raise-debt-products-tabs">
          {[
            { k: "individual", label: "For Individuals", icon: Home },
            { k: "business", label: "For Businesses", icon: Briefcase },
          ].map(({ k, label, icon: Icon }) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`raise-debt-tab-${k}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-heading text-sm transition-all ${
                tab === k
                  ? "bg-navy text-white shadow-[0_10px_30px_rgba(15,29,64,0.25)]"
                  : "bg-navy/[0.04] text-ink-muted hover:bg-navy/10"
              }`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map(({ key, icon: Icon, title, tag, pitch }) => (
            <div key={key} className="border border-navy/10 rounded p-5 bg-white/70 hover:bg-white hover:border-navy/25 transition-colors group" data-testid={`raise-debt-product-${key}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 rounded bg-navy/[0.06] flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                  <Icon size={18} className="text-navy" />
                </div>
                <Pill tone="muted">{tag}</Pill>
              </div>
              <div className="font-heading text-lg text-ink">{title}</div>
              <p className="mt-1.5 text-sm text-ink-soft leading-relaxed">{pitch}</p>
              <a href="#lead-form" className="mt-3 inline-flex items-center gap-1 text-xs font-heading text-navy hover:text-gold transition-colors">
                Talk to an advisor <ChevronRight size={12} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Free CIBIL callout                                                  */
/* ------------------------------------------------------------------ */
function FreeCibilCallout() {
  return (
    <section className="brand-section-alt" data-testid="raise-debt-free-cibil">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="border border-gold/40 rounded-lg bg-gradient-to-r from-gold/15 via-white/50 to-white/60 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-12 h-12 shrink-0 rounded-full bg-gold/30 flex items-center justify-center">
              <Gift className="text-[#8A6600]" size={22} />
            </div>
            <div>
              <div className="text-[10px] font-mono tracking-widest uppercase text-[#8A6600] mb-1">Free · limited time</div>
              <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight">
                Check your CIBIL score — <span className="text-[#8A6600]">worth ₹999, free for you</span>
              </h3>
              <p className="mt-2 text-sm text-ink-soft max-w-xl">
                Know exactly where you stand before applying. Consented soft pull — no impact on your score,
                and we&apos;ll walk you through what each flag means and how to improve.
              </p>
            </div>
          </div>
          <a href="#lead-form" className="shrink-0 px-6 py-3 bg-navy text-white font-heading rounded-md hover:bg-navy/90 inline-flex items-center gap-2 shadow-[0_10px_28px_rgba(15,29,64,0.18)]"
            data-testid="raise-debt-free-cibil-cta">
            Claim my free score <ArrowRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}


/* ------------------------------------------------------------------ */
/* How it works                                                        */
/* ------------------------------------------------------------------ */
function HowItWorks() {
  return (
    <section id="how" className="relative brand-section-primary overflow-hidden" data-testid="raise-debt-how">
      <Idea size={180} color="#FFFFFF" opacity={0.12} strokeWidth={1.4}
        className="absolute top-10 right-10 hidden lg:block pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="max-w-3xl mb-12 text-white">
          <div className="text-[10px] font-mono tracking-widest uppercase text-white/70 mb-2">
            HOW IT WORKS · 6 STAGES · FULL TRANSPARENCY
          </div>
          <h2 className="font-display text-4xl sm:text-5xl text-white leading-tight">
            You&apos;ll always know what stage your case is on.
          </h2>
          <p className="mt-3 text-white/80 text-base leading-relaxed">
            No black boxes. Every submission, every lender query, every offer — live in your portal.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="border border-white/15 bg-white/[0.04] backdrop-blur-sm rounded p-5"
              data-testid={`raise-debt-step-${s.n}`}
            >
              <div className="font-display text-gold text-3xl">{s.n}</div>
              <div className="font-heading text-lg text-white mt-1">{s.title}</div>
              <p className="mt-2 text-sm text-white/75 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Eligibility & docs                                                  */
/* ------------------------------------------------------------------ */
function EligibilityAndDocs() {
  const ELIG = [
    "Business vintage ≥ 2 years (1 year for RBF)",
    "Annual revenue ≥ ₹1 Cr (₹5 Cr for term loans)",
    "GST + banking + income tax filings up to date",
    "Promoter CIBIL ≥ 700 for unsecured products",
    "Clean legal / no active NPAs on principal borrower",
  ];
  const DOCS = [
    "KYC of entity + promoters (PAN, Aadhaar, GST, CIN)",
    "Last 2 years audited financials + provisional current year",
    "12 months bank statements (all operating accounts)",
    "GST returns (last 12 months) + ITR (last 2 years)",
    "Sanction letters for any existing debt facilities",
    "Product-specific: collateral papers, invoices, purchase orders",
  ];
  return (
    <section className="brand-section-alt border-t border-navy/[0.06]" data-testid="raise-debt-elig">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="border border-navy/10 rounded p-6 bg-white/70">
          <div className="text-[10px] font-mono tracking-widest uppercase text-ink-muted mb-1">ELIGIBILITY · WHO LENDERS ON VENTURAZ SAY YES TO</div>
          <h3 className="font-display text-2xl text-ink">Who typically qualifies</h3>
          <ul className="mt-4 space-y-2">
            {ELIG.map((e, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-ink-soft">
                <CheckCircle2 size={14} className="text-emerald-700 mt-0.5 shrink-0" />
                <span>{e}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 text-[10px] font-mono text-ink-muted">
            Don&apos;t match all? Talk to us anyway — we&apos;ll take your story to the right lender, and their credit heads have room for the right exception.
          </div>
        </div>
        <div className="border border-navy/10 rounded p-6 bg-white/70">
          <div className="text-[10px] font-mono tracking-widest uppercase text-ink-muted mb-1">DOCUMENTS · CORE CHECKLIST</div>
          <h3 className="font-display text-2xl text-ink">What lenders will ask for</h3>
          <ul className="mt-4 space-y-2">
            {DOCS.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-ink-soft">
                <FileText size={14} className="text-navy mt-0.5 shrink-0" />
                <span>{d}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 text-[10px] font-mono text-ink-muted">
            Upload once — we share with only the shortlisted lenders. Never dropped into a broker distribution list.
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* EMI calculator                                                      */
/* ------------------------------------------------------------------ */
function EmiCalculator() {
  const [amount, setAmount] = useState(10000000); // ₹1 Cr
  const [rate, setRate] = useState(12);
  const [tenure, setTenure] = useState(60);
  const emi = useMemo(() => {
    const r = (Number(rate) || 0) / 12 / 100;
    const n = Number(tenure) || 1;
    const p = Number(amount) || 0;
    if (r === 0) return p / n;
    return (p * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  }, [amount, rate, tenure]);
  const total = emi * (Number(tenure) || 0);
  const interest = total - Number(amount);

  return (
    <section id="emi" className="brand-section-alt" data-testid="raise-debt-calc">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
        <div>
          <div className="text-[10px] font-mono tracking-widest uppercase text-ink-muted mb-2">CALCULATOR · INDICATIVE ONLY</div>
          <h2 className="font-display text-4xl sm:text-5xl text-ink leading-tight">
            Play with the numbers.
          </h2>
          <p className="mt-3 text-ink-soft text-base leading-relaxed max-w-lg">
            A quick EMI calculator to see what your monthly outflow looks like.
            Actual rates depend on your product, security and credit profile —
            these are directional only.
          </p>
        </div>
        <div className="border border-navy/10 rounded bg-white/80 p-6 space-y-4">
          <Slider label="Loan amount" value={amount} min={2500000} max={500000000} step={500000}
            onChange={setAmount} format={fmtCr} testId="calc-amount" />
          <Slider label="Interest rate (% p.a.)" value={rate} min={7} max={22} step={0.25}
            onChange={setRate} format={(v) => `${Number(v).toFixed(2)}%`} testId="calc-rate" />
          <Slider label="Tenure (months)" value={tenure} min={12} max={240} step={6}
            onChange={setTenure} format={(v) => `${v} m`} testId="calc-tenure" />
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-navy/[0.06]">
            <CalcOut label="Monthly EMI" value={fmtCr(Math.round(emi))} tone="gold" testId="calc-emi" />
            <CalcOut label="Total interest" value={fmtCr(Math.round(interest))} testId="calc-interest" />
            <CalcOut label="Total payable" value={fmtCr(Math.round(total))} testId="calc-total" />
          </div>
          <a href="#lead-form" className="w-full block text-center py-2.5 bg-navy text-white text-sm font-heading rounded hover:bg-navy/90"
            data-testid="calc-cta">
            Get an actual quote → <ArrowRight className="inline" size={14} />
          </a>
        </div>
      </div>
    </section>
  );
}
function Slider({ label, value, min, max, step, onChange, format, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</div>
        <div className="font-heading text-sm text-ink">{format(value)}</div>
      </div>
      <input type="range" value={value} min={min} max={max} step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        onInput={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
        className="w-full accent-navy" />
    </div>
  );
}
function CalcOut({ label, value, tone, testId }) {
  return (
    <div className="text-center">
      <div className={`font-display text-xl ${tone === "gold" ? "text-[#8A6600]" : "text-ink"}`} data-testid={testId}>{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mt-0.5">{label}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Social proof                                                        */
/* ------------------------------------------------------------------ */
function SocialProof() {
  const cases = [
    { sector: "Individual · Home Loan", stage: "Bengaluru", amount: "₹85 L", product: "Home loan @ 8.4% · 25 yrs", note: "Negotiated 40 bps off the sanctioned rate on a ₹1.2 Cr property. Buyer moved in 21 days after enquiry." },
    { sector: "Individual · Car Loan", stage: "Mumbai", amount: "₹18 L", product: "Car loan @ 8.65% · 5 yrs", note: "100% on-road funding for a family SUV. Loan sanctioned Friday evening, keys collected Saturday morning." },
    { sector: "Business · Manufacturing", stage: "Established", amount: "₹85 Cr", product: "Term loan + LAP", note: "Refinanced legacy debt from 14% to 10.75%, unlocked ₹22 Cr of working capital." },
    { sector: "Business · D2C", stage: "Growth", amount: "₹18 Cr", product: "RBF + working capital", note: "Sanctioned in 34 days across two NBFCs. Founder retained 100% equity." },
    { sector: "Individual · Wedding", stage: "Delhi NCR", amount: "₹12 L", product: "Personal loan @ 10.5%", note: "Funded a dream Udaipur wedding — approved in 26 hours, credited before the sangeet." },
    { sector: "Business · SaaS", stage: "Series B", amount: "₹42 Cr", product: "Venture debt", note: "Non-dilutive runway extension of 14 months — closed one week before quarter-end." },
  ];
  return (
    <section className="brand-section-alt border-t border-navy/[0.06]" data-testid="raise-debt-proof">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="max-w-3xl mb-10">
          <div className="text-[10px] font-mono tracking-widest uppercase text-ink-muted mb-2">CASE STUDIES · ANONYMISED</div>
          <h2 className="font-display text-4xl sm:text-5xl text-ink leading-tight">
            Recent dreams, funded.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cases.map((c, i) => (
            <div key={i} className="border border-navy/10 rounded p-5 bg-white/70" data-testid={`raise-debt-case-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <Pill tone="gold">{c.sector}</Pill>
                <span className="text-[10px] font-mono text-ink-muted">{c.stage}</span>
              </div>
              <div className="font-display text-3xl text-ink">{c.amount}</div>
              <div className="text-[10px] font-mono text-ink-muted mt-1 uppercase tracking-widest">{c.product}</div>
              <p className="mt-3 text-sm text-ink-soft leading-relaxed">&ldquo;{c.note}&rdquo;</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* FAQ                                                                 */
/* ------------------------------------------------------------------ */
function FaqSection({ openFaq, setOpenFaq }) {
  return (
    <section id="faq" className="brand-section-alt border-t border-navy/[0.06]" data-testid="raise-debt-faq">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
        <div className="max-w-3xl mb-10">
          <div className="text-[10px] font-mono tracking-widest uppercase text-ink-muted mb-2">FAQ · STRAIGHT ANSWERS</div>
          <h2 className="font-display text-4xl sm:text-5xl text-ink leading-tight">
            Questions we get most weeks.
          </h2>
        </div>
        <div className="max-w-3xl divide-y divide-navy/[0.08] border border-navy/10 rounded bg-white/70">
          {FAQ.map((f, i) => (
            <button key={i} onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
              className="w-full text-left px-5 py-4" data-testid={`raise-debt-faq-${i}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="font-heading text-ink text-base">{f.q}</div>
                <ChevronDown size={16} className={`shrink-0 text-ink-muted transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </div>
              {openFaq === i && <p className="mt-2 text-sm text-ink-soft leading-relaxed">{f.a}</p>}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Final CTA                                                           */
/* ------------------------------------------------------------------ */
function FinalCta() {
  return (
    <section className="relative brand-section-primary overflow-hidden" data-testid="raise-debt-final-cta">
      <Network size={220} color="#FFD700" opacity={0.18} strokeWidth={1.5}
        className="absolute top-8 right-10 hidden md:block pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 text-center">
        <h2 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-tight">
          Your dream, our expertise.
        </h2>
        <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl mx-auto">
          Free CIBIL check worth ₹999. Free advisor call within one business day.
          Compare 85+ lenders in one place. Zero platform fees, ever.
        </p>
        <div className="mt-8">
          <a href="#lead-form" className="inline-block px-8 py-4 bg-gold text-navy font-heading font-semibold rounded shadow-[0_14px_36px_rgba(255,215,0,0.35)] hover:bg-gold-hover transition-all"
            data-testid="raise-debt-final-cta-btn">
            Get free consultation &nbsp;<ArrowRight className="inline" size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}


/* ------------------------------------------------------------------ */
/* MoneyVideoOverview — "How VenturaZ Money works" section band       */
/* ------------------------------------------------------------------ */
function MoneyVideoOverview() {
  const cfg = useHeroVideoConfig("money");
  if (cfg.enabled === false) return null;
  return (
    <section className="brand-section-alt border-t border-navy/[0.06]" data-testid="raise-debt-video-overview">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.28em] text-[#8A6600]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#B8860B] animate-pulse" /> See it in motion
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl text-ink mt-3 leading-tight">
            {cfg.title}
          </h2>
          <p className="text-sm text-ink-muted mt-3 leading-relaxed">
            One quick walk-through of how we compare 85+ lenders, pick the sharpest rate,
            and get you funded in as little as 72 hours.
          </p>
        </div>
        <div className="max-w-5xl mx-auto">
          <VideoPreview
            palette="copper"
            src={cfg.url || undefined}
            title={cfg.title}
            description={cfg.description}
            eyebrow="Loan advisor tour"
            duration={cfg.duration}
            testId="raise-debt-video-overview-preview"
          />
          <p className="mt-4 text-center text-[11px] font-mono uppercase tracking-widest text-ink-muted">
            No signup needed · plays in a lightbox · closes on Esc
          </p>
        </div>
      </div>
    </section>
  );
}
